import { useCallback, useMemo, useState } from "react";
import { ConfigType, baseOptionsKey, defaultConfig, sdkOptionsKey } from "./EmbedConfigContext";
import { merge } from "lodash";
import { getSdkSource } from "./sdkEnvironment";

const storeKey = "glean-sdk-config";

const parseNestedJson = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(parseNestedJson);
    }

    if (value !== null && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value).map(([key, nestedValue]) => [key, parseNestedJson(nestedValue)])
        );
    }

    if (typeof value === "string") {
        try {
            const parsed: unknown = JSON.parse(value);

            // Only promote encoded objects and arrays. Booleans, numbers, and strings should be left alone
            if (parsed !== null && typeof parsed === "object") {
                return parseNestedJson(parsed);
            }
        } catch {
            // Most strings aren't encoded JSON, leave them unchanged.
        }
    }

    return value;
}

const parseConfig = (value: string | null): Partial<ConfigType> | undefined => {
    if (!value) return undefined;

    try {
        const parsed: unknown = parseNestedJson(JSON.parse(value));
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            throw new Error("config must be a JSON object");
        }
        return parsed as Partial<ConfigType>;
    } catch (error) {
        console.error("Unable to parse playground configuration; using the existing config.", error);
        return undefined;
    }
}

const shouldReload = (prevValue: ConfigType, newValue: ConfigType) => {
    const baseOptionReloadKeys = ['backend', 'webAppUrl'] as const;
    for (const key of baseOptionReloadKeys) {
        if (prevValue[baseOptionsKey][key] !== newValue[baseOptionsKey][key]) {
            return true
        }
    }

    const sdkOptionReloadKeys = ['source', 'integrity'] as const;
    for (const key of sdkOptionReloadKeys) {
        if (prevValue[sdkOptionsKey][key] !== newValue[sdkOptionsKey][key]) {
            return true
        }
    }
    return false
}

const useConfigStore = () => {
    const [config, setConfig] = useState<ConfigType>(() => {
        const value = window.sessionStorage.getItem(storeKey)
        const storedConfig = value ? parseConfig(value) : undefined;
        const queryParams = new URLSearchParams(window.location.search);
        const queryConfig = parseConfig(queryParams.get("config"));
        const querySdkSource = getSdkSource(queryParams.get("env"));

        const configsToMerge: Array<Partial<ConfigType> | undefined> = [defaultConfig];

        // Query configs replace stored options, but retain the selected SDK environment unless explicitly overridden.
        if (queryConfig) {
            console.log("Parsed config query parameter options:", queryConfig);
            configsToMerge.push({ [sdkOptionsKey]: storedConfig?.[sdkOptionsKey] }, queryConfig);
        } else if (storedConfig) {
            configsToMerge.push(storedConfig);
        }

        const mergedConfig: ConfigType = merge({}, ...configsToMerge);
        if (querySdkSource) {
            mergedConfig[sdkOptionsKey].source = querySdkSource;
        }

        return mergedConfig;
    });

    const setPersistentConfig = useCallback((newValue: ConfigType) => setConfig((prevValue: ConfigType) => {
        window.sessionStorage.setItem(storeKey, JSON.stringify(newValue));
        // Reload page if backend URL or webAppUrl changes
        if (shouldReload(prevValue, newValue)) {
            window.location.reload();
        }
        return newValue;
    }), [setConfig]);

    window.setConfig = setPersistentConfig;
    return useMemo(() => ({config, setConfig: setPersistentConfig}), [config, setPersistentConfig]);
}

export default useConfigStore;
